import { Avatar, Box, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { IconButton } from 'utoniq-ui'

import { Maybe, User } from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { ThreadCategory } from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'

import { ThreadFormValues } from './ThreadCreateForm'

export type ThreadCreatePreviewProps = {
  artistId?: string
  categoryOptions: { label: string; value: string }[]
  user: Maybe<Pick<User, 'id' | 'displayName' | 'avatar'>>
  thread: ThreadFormValues
  onClose: () => void
}

export const ThreadCreatePreview: Organism<ThreadCreatePreviewProps> = ({
  artistId,
  thread,
  user,
  categoryOptions,
  onClose,
}) => {
  const { t } = useTranslation('threadCreate')
  return (
    <ArtistThemeProvider artistId={artistId}>
      <BaseLayout
        header={{
          left: (
            <IconButton.Back edge="start" size="medium" onClick={onClose} />
          ),
          title: t('titlePreview'),
        }}
      >
        <ThreadTitleCell
          thread={thread}
          user={user}
          categoryOptions={categoryOptions}
        />
        <ThreadTextAreaCell description={thread.description} />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type ThreadTitleProps = {
  thread: ThreadCreatePreviewProps['thread']
  categoryOptions: ThreadCreatePreviewProps['categoryOptions']
  user: ThreadCreatePreviewProps['user']
}
const ThreadTitleCell: Cell<ThreadTitleProps> = ({
  thread,
  user,
  categoryOptions,
}) => {
  const category = useMemo(
    () => categoryOptions?.find(category => category.value === thread.category),
    [categoryOptions, thread.category]
  )
  return (
    <CellFrame>
      {thread.thumbnail?.fileUrl && (
        <Avatar
          src={thread.thumbnail?.fileUrl}
          variant="square"
          sx={{ width: '100%', height: 281, pb: 5 }}
        />
      )}
      <Box sx={{ px: 5 }}>
        <Typography variant="h4">{thread.name}</Typography>
        {!!category && (
          <ThreadCategory
            category={category.label}
            isOfficial={false}
            sx={{ mt: 2 }}
          />
        )}
        <Stack sx={{ pt: 3 }} spacing={2} direction="row" alignItems="center">
          <Avatar
            src={user?.avatar?.smallUri ?? ''}
            sx={{ width: 28, height: 28 }}
          />
          <Typography variant="subtitle2" color="text.secondary">
            {user?.displayName}
          </Typography>
        </Stack>
      </Box>
    </CellFrame>
  )
}

type ThreadTextAreaCellProps = {
  description: string
}
const ThreadTextAreaCell: Cell<ThreadTextAreaCellProps> = ({ description }) => (
  <CellFrame sx={{ px: 5, pt: 3, pb: 5 }}>
    <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
      {description}
    </Typography>
  </CellFrame>
)
